import { UserOutlined } from "@ant-design/icons";
import {
  Card,
  Divider,
  Flex,
  Form,
  Image,
  Input,
  Select,
  Typography,
  Button,
  Avatar,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { translate } from "shared/languages";
import { Business, RegisterBusinessForm } from "shared/types";
import { BusinessType } from "shared/types/enum";
import { useState } from "react";
import { useUpdateBusiness } from "hooks/mutations/business";
import PhoneInput, { phoneRules } from "shared/components/PhoneInput";

const { Option } = Select;

interface BusinessProfileTabProps {
  business: Business;
}

const BusinessProfile = ({ business }: BusinessProfileTabProps) => {
  const [businessForm] = useForm();
  const [isFieldChanged, setIsFieldChanged] = useState(false);
  const { mutate: updateBusiness, isPending } = useUpdateBusiness();

  const submitFieldChange = () => {
    setIsFieldChanged(true);
  };

  const handleSubmitChange = (values: RegisterBusinessForm) => {
    updateBusiness(
      { businessId: business.id, form: values },
      { onSuccess: () => setIsFieldChanged(false) },
    );
  };

  return (
    <Card>
      <Flex vertical gap={20}>
        <Flex vertical>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {translate("title.business_profile") || "Business Profile"}
          </Typography.Title>
          <Typography>
            {translate("subtitle.business_profile") ||
              "Manage your business information"}
          </Typography>
        </Flex>

        <Form
          form={businessForm}
          name="business-profile"
          labelAlign="left"
          labelCol={{ span: 6 }}
          colon={false}
          requiredMark={false}
          onFinish={handleSubmitChange}
          initialValues={{
            name: business.name,
            email: business.email,
            phone_number: business.phone_number,
            address: business.address,
            business_type: business.business_type,
            legal_name: business.legal_name,
            npwp: business.npwp,
          }}
          onFieldsChange={submitFieldChange}
        >
          <Flex>
            {business.photo_url ? (
              <Avatar src={<Image src={business.photo_url} alt="avatar" />} />
            ) : (
              <Avatar size={50} icon={<UserOutlined />} />
            )}
          </Flex>

          <Divider />

          <Form.Item
            name="name"
            label={translate("label.name") || "Business Name"}
            rules={[
              { required: true, message: "Please input your business name!" },
            ]}
          >
            <Input placeholder="Enter business name" />
          </Form.Item>

          <Form.Item name="legal_name" label={translate("label.legal_name")}>
            <Input placeholder="PT ..." />
          </Form.Item>

          {/* No placeholder: whoever fills this in already knows the
              studio's NPWP, and the field has no format validation to hint
              at — a full 16-digit example reads as clutter, not guidance. */}
          <Form.Item name="npwp" label={translate("label.npwp")}>
            <Input />
          </Form.Item>

          <Divider />

          <Form.Item
            name="email"
            label={translate("label.email") || "Email"}
            rules={[
              {
                type: "email",
                message:
                  translate("form.error.email") ||
                  "Please enter a valid email!",
              },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="phone_number"
            label={translate("label.phone_number") || "Phone Number"}
            rules={phoneRules()}
          >
            <PhoneInput
              placeholder={translate("form.placeholders.phone_number")}
            />
          </Form.Item>

          <Divider />

          <Form.Item
            name="address"
            label={translate("label.address") || "Address"}
            rules={[{ required: true, message: "Please input your address!" }]}
          >
            <Input.TextArea placeholder="Enter business address" />
          </Form.Item>

          <Form.Item
            name="business_type"
            label={translate("label.business_type") || "Business Type"}
            rules={[
              { required: true, message: "Please select your business type!" },
            ]}
          >
            <Select placeholder="Select business type">
              {Object.keys(BusinessType).map((business_type) => {
                return (
                  <Option key={business_type} value={business_type}>
                    {business_type.toUpperCase()}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isPending}
              disabled={!isFieldChanged}
            >
              {translate("button.save_changes") || "Save Changes"}
            </Button>
          </Form.Item>
        </Form>
      </Flex>
    </Card>
  );
};

export default BusinessProfile;
